import React, { useEffect, useState, useRef } from 'react';
import { TileLayout } from "@progress/kendo-react-layout";
import { useWindowSize } from "usehooks-ts";
import EndpointMappingTableComponent from './Tables/EndpointMappingTableComponent';
import { Loader } from '@progress/kendo-react-indicators';
import AssetTemplateReadDto, { Groups, Endpoints, Gateways } from '../../../Models/Assets/AssetTemplateReadDto';
import LoadingSpinner from '../../Shared/Components/LoadingSpinner';
import ErrorCard from '../../Shared/Components/Cards/ErrorCard';
import AssetImageComponent from './Images/AssetImageComponent';
import GroupsTableComponent from './Tables/GroupsTableComponent';
import { useAssetConfigurationQueries } from '../Hooks/useAssetConfigurationQueries';
import { buildUpdatedTemplate, hasTemplateChanged, getColumnsDisplayPosition, withoutDeletedGroups, withoutDeletedGroupEndpoints } from '../Utilities/AssetConfigurationUtils';
import GatewayMappingTableComponent from './Tables/GatewayMappingTableComponent';

interface Props {
  selectedTemplate: any;
  groups: Groups[];
  setGroups: React.Dispatch<React.SetStateAction<Groups[]>>;
  endpoints: Endpoints[];
  setEndpoints: React.Dispatch<React.SetStateAction<Endpoints[]>>;
  greasePumps: Groups[];
  setGreasePumps: React.Dispatch<React.SetStateAction<Groups[]>>;
  selectedGateway: { id: string; serialNumber: string };
  onGatewayChange: (gateway: { id: string; serialNumber: string }) => void;
  gateways: Gateways[];
  setGateways: React.Dispatch<React.SetStateAction<Gateways[]>>;
  onTemplateChange: (updatedTemplate: AssetTemplateReadDto) => void;
  onGroupImageEditStateChange?: (isEditing: boolean) => void;
  isEdit: boolean;
}

const AssetConfigurationComponent: React.FC<Props> = ({
  selectedTemplate,
  groups,
  setGroups,
  endpoints,
  setEndpoints,
  greasePumps,
  setGreasePumps,
  selectedGateway,
  onGatewayChange,
  gateways,
  setGateways,
  onTemplateChange,
  onGroupImageEditStateChange,
  isEdit
}) => {
  const { height, width } = useWindowSize();
  const containerHeight = height - 270;
  const isSingleColumn = width < 768;
  const rowHeight = 25;

  // Usar hook customizado para queries
  const {
    templateConfigQuery,
    assetImageQuery,
    unmappedEndpointDevicesQuery,
    requirementsQuery,
    gatewaysQuery,
  } = useAssetConfigurationQueries(isEdit, selectedTemplate, selectedTemplate);

  // Use correct template data depending on mode
  const finalTemplateData = isEdit ? selectedTemplate : templateConfigQuery.data;

  // Group selection logic
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => {
    const firstGroup = withoutDeletedGroups(finalTemplateData?.groups || [])[0];
    return firstGroup ? firstGroup.id : null;
  });

  // Track initialization to prevent infinite loops
  const initializedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!finalTemplateData?.id) return;
    
    // Only initialize once per template
    if (initializedRef.current === finalTemplateData.id) return;
    
    initializedRef.current = finalTemplateData.id;
    
    // Set initial group selection only if none (preserves selection when navigating between steps)
    if (!selectedGroupId) {
      setSelectedGroupId(withoutDeletedGroups(finalTemplateData.groups || [])[0]?.id || null);
    }
    
    // Initialize from template only when parent has no data (preserves dropdown selections when navigating between steps)
    if (finalTemplateData.groups && groups.length === 0) {
      setGroups(withoutDeletedGroups(finalTemplateData.groups).map((g: Groups) => ({
        ...g,
        status: g.status ?? 'Active'
      })));
    }
    if (finalTemplateData.endpoints && endpoints.length === 0) {
      setEndpoints(
        withoutDeletedGroupEndpoints(finalTemplateData.endpoints, finalTemplateData.groups || []).map((ep: Endpoints) => ({
          ...ep,
          status: ep.status ?? 'Active'
        }))
      );
    }
    if (finalTemplateData.greasePumps && greasePumps.length === 0) {
      setGreasePumps(finalTemplateData.greasePumps.map((p: any) => ({
        ...p,
        status: p.status ?? 'Active'
      })));
    }
    if (finalTemplateData.gateways && gateways.length === 0) {
      setGateways(finalTemplateData.gateways.map((g: Gateways) => ({
        ...g,
        status: g.status ?? 'Active'
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTemplateData?.id, selectedGroupId]);

  // Update the complete template whenever any component changes
  useEffect(() => {
    if (!finalTemplateData) return;
    
    const updatedTemplate = buildUpdatedTemplate(finalTemplateData, groups, endpoints, gateways);
    
    // Only call onTemplateChange if the template has actually changed
    if (hasTemplateChanged(updatedTemplate, finalTemplateData)) {
      onTemplateChange?.(updatedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, endpoints, gateways, finalTemplateData?.id]);

  // Keep selection valid when a group is dropped (deleted in the template)
  useEffect(() => {
    if (selectedGroupId === '0' || selectedGroupId === null || groups.length === 0) return;
    if (!groups.some(g => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  const handleGroupSelect = (groupId: string | null) => {
    setSelectedGroupId(groupId);
  };

  const handleMarkersChange = (updatedMarkers: any[]) => {
    const updatedGroups = groups.map(group => {
      const marker = updatedMarkers.find(m => m.id === group.id);
      if (marker) {
        return {
          ...group,
          xPos: marker.position.xPos,
          yPos: marker.position.yPos
        };
      }
      return group;
    });
    setGroups(updatedGroups);
  };

  const handleGroupsTableChange = (updatedGroups: Groups[]) => {
    const isGroupActive = (g: Groups) => (g as Groups & { status?: string }).status === 'Active';
    const isGroupInactiveOrDeleted = (g: Groups) => (g as Groups & { status?: string }).status === 'Inactive' || (g as Groups & { status?: string }).status === 'Deleted';
    const disabledGroupIds = updatedGroups
      .filter(ug => isGroupInactiveOrDeleted(ug))
      .filter(ug => {
        const prev = groups.find(g => g.id === ug.id);
        return prev && isGroupActive(prev);
      })
      .map(ug => ug.id);
    const enabledGroupIds = updatedGroups
      .filter(ug => isGroupActive(ug))
      .filter(ug => {
        const prev = groups.find(g => g.id === ug.id);
        return prev && isGroupInactiveOrDeleted(prev);
      })
      .map(ug => ug.id);

    if (disabledGroupIds.length > 0 || enabledGroupIds.length > 0) {
      setEndpoints(prevEndpoints =>
        prevEndpoints.map(ep => {
          if (disabledGroupIds.includes(ep.groupId)) return { ...ep, status: 'Inactive' as const };
          if (enabledGroupIds.includes(ep.groupId)) return { ...ep, status: 'Active' as const };
          return ep;
        })
      );
    }

    const mergedGroups = updatedGroups.map(updatedGroup => {
      const existingGroup = groups.find(g => g.id === updatedGroup.id);
      return {
        ...updatedGroup,
        xPos: existingGroup?.xPos || updatedGroup.xPos,
        yPos: existingGroup?.yPos || updatedGroup.yPos
      };
    });
    setGroups(mergedGroups);
  };

  // A grease pump can only point to a gateway from this step: clear the ones whose device is no longer listed
  const handleGatewaysChange = (updatedGateways: Gateways[]) => {
    const nextGatewayIds = new Set(updatedGateways.map(gw => gw.id).filter(Boolean));
    const removedGatewayIds = gateways
      .map(gw => gw.id)
      .filter(id => id && !nextGatewayIds.has(id));

    if (removedGatewayIds.length > 0) {
      setGreasePumps(prevGreasePumps =>
        prevGreasePumps.map((pump: any) =>
          removedGatewayIds.includes(pump.deviceId)
            ? { ...pump, deviceId: '', serialNo: '' }
            : pump
        )
      );
    }

    setGateways(updatedGateways);
  };

  const handleGatewayChange = (e: any) => {
    if (!e.value || e.value.id === '') {
      onGatewayChange({ id: '', serialNumber: '' });
    } else {
      onGatewayChange({
        id: e.value.id,
        serialNumber: e.value.serialNumber
      });
    }
  };

  const tileData = [
    {
      body: (
        <div className="h-full flex flex-col">
          {assetImageQuery.isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader type="pulsing" />
            </div>
          ) : assetImageQuery.data?.data ? (
            <AssetImageComponent
              key={`${finalTemplateData?.id}-${finalTemplateData?.imageId}`}
              assetData={{
                id: finalTemplateData?.id || '',
                name: finalTemplateData?.name || '',
                description: finalTemplateData?.description || '',
                imageId: finalTemplateData?.imageId || '',
                isGreasePumpConnectivityEnabled: finalTemplateData?.isGreasePumpConnectivityEnabled || false,
                endpoints: finalTemplateData?.endpoints || [],
                gateways: gateways.length > 0 ? gateways : (finalTemplateData?.gateways || []),
                groups,
              }}
              selectedGroupId={selectedGroupId}
              onMarkerSelect={handleGroupSelect}
              assetImageUrl={assetImageQuery.data.data}
              onMarkersChange={handleMarkersChange}
              templateName={finalTemplateData?.name}
              onEditStateChange={onGroupImageEditStateChange}
            />
          ) : (
            <div className="flex justify-center items-center h-full text-gray-500">
              No image available
            </div>
          )}
        </div>
      ),
      reorderable: false,
    },
    {
      body: (
        <GatewayMappingTableComponent
          gateways={gateways}
          selectedGateway={selectedGateway}
          onGatewayChange={handleGatewayChange}
          onGatewaysChange={handleGatewaysChange}
          gatewaysQuery={gatewaysQuery}
        />
      ),
      reorderable: false,
    },
    {
      body: (
        <div className="h-full overflow-hidden">
          <GroupsTableComponent
            groups={groups}
            onGroupsChange={handleGroupsTableChange}
            assetData={finalTemplateData || undefined}
            onGroupSelect={handleGroupSelect}
            selectedGroupId={selectedGroupId}
          />
        </div>
      ),
      reorderable: false,
    },
    {
      body: (
        <div className="h-full overflow-auto overscroll-contain">
          <EndpointMappingTableComponent
            assetData={finalTemplateData || undefined}
            selectedGroupId={selectedGroupId}
            endpoints={endpoints}
            onEndpointsChange={setEndpoints}
            serialNumbers={unmappedEndpointDevicesQuery.data!}
            requirements={requirementsQuery.data || []}
            groups={groups}
          />
        </div>
      ),
      reorderable: false,
    },
  ];

  const columnsDisplayPosition = getColumnsDisplayPosition(isSingleColumn);

  if (
    templateConfigQuery.isLoading ||
    assetImageQuery.isLoading ||
    templateConfigQuery.isFetching ||
    assetImageQuery.isFetching ||
    unmappedEndpointDevicesQuery.isLoading ||
    unmappedEndpointDevicesQuery.isFetching ||
    requirementsQuery.isLoading ||
    requirementsQuery.isFetching ||
    gatewaysQuery.isLoading ||
    gatewaysQuery.isFetching
  ) {
    return <LoadingSpinner />
  } else if (
    templateConfigQuery.error ||
    assetImageQuery.error ||
    unmappedEndpointDevicesQuery.error ||
    requirementsQuery.error ||
    gatewaysQuery.error
  ) {
    return <ErrorCard />
  }

  return (
    <div className="w-full overflow-y-auto overscroll-contain" style={{ height: containerHeight }}>
      <TileLayout
        columns={isSingleColumn ? 1 : 2}
        rowHeight={rowHeight}
        positions={columnsDisplayPosition}
        gap={{ rows: 10, columns: 10 }}
        items={tileData}
        className="w-full dashboard-page-tile"
        style={{
          width: '100%',
          height: isSingleColumn ? 'auto' : '100%'
        }}
      />
    </div>
  );
};

export default AssetConfigurationComponent; 